"use client";

import { Button } from "@/components/themes/ui/button";
import Link from "next/link";
import ImageGallery from "@/components/themes/ImageGallery";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CollapsibleSections from "@/components/themes/CollapsibleSections";
import { MdShoppingCart } from "react-icons/md";
import RelatedProducts from "@/components/products/RelatedProducts";
import Share from "@/components/themes/Share";
import { useRouter, useSearchParams } from "next/navigation";
import { useUser } from "@/providers/userContext";
import toast, { Toaster } from "react-hot-toast";
import {
  addtocartclicks,
  variationsRender,
} from "@/clicks/productinfoClicks";
import {
  axiosGetProductReviews,
  axiosGetWishlist,
} from "@/utilities/axioscall";
import { addtowishlist } from "@/clicks/wishlistClicks";
import { APPENUMS } from "@/constants";
import { FBPIXPush } from "@/services/fbConversionService";
import { ShoppingCart } from "lucide-react";
import SvgIcons from "../themes/SvgIcons";
import { getCurrencySymbol } from "@/services/storeService";

export const revalidate = 60;
export const runtime = "edge";

interface Product {
  [x: string]: any;
  id: string;
  name: string;
  eng_name: string;
  category: string;
  subcategory: string | null;
  imageurl: string | string[];
  selling_price: string;
  mrp: string;
  discount: string;
  star_rating: number;
  description: string;
  color_options: string[];
  size_options: string[];
  seo_url: string;
  meta_tag_title?: string;
  meta_tag_desc?: string;
  sp: string;
  category_name: string;
  images: string[];
  colorcodes_id: string;
  size_id: string;
  weight_id: string;
  quantity: number;
  addtocart?: boolean;
  shrtdesc: string;
  descp: string;
  variation?: string;
  storeid?: string;
  esin?: string;
  products_id?: string;
  category_id?: string;
  sortorder?: number;
  city_shipping?: string;
  state_shipping?: string;
  national_shipping?: string;
  delivery_time?: string;
  status?: string;
  is_featured?: boolean;
  visibility?: string;
  customizable?: boolean;
  weight?: string;
}

interface MediaItem {
  id: string;
  type: "image" | "video";
  src: string;
  alt: string;
}

interface AvailableData {
  colorcodes: any[];
  size: any[];
  products: any[];
  weight: any[];
  gravingext: string;
}

interface ProductInfocartViewProps {
  params: { slug?: string };
  productsListData: any[];
  productinfodata: Product;
  variationsListData: any[];
  colorcodesListData: any[];
  sizeListData: any[];
  weightListData: any[];
  productgroups: any[];
}

export default function ProductInfocartView({
  params = {},
  productsListData,
  productinfodata,
  variationsListData,
  colorcodesListData,
  sizeListData,
  weightListData,
  productgroups,
}: ProductInfocartViewProps) {
  const slug = params.slug || "";
  if (!slug) console.error("Slug is missing in params");

  const [isHeartAnimating, setIsHeartAnimating] = useState(false);
  const handleWishlistClick = () => {
    setIsHeartAnimating(true);
    addtowishlist(productinfo, productWishlistData, setProductWishlistData, router.push);
    setTimeout(() => setIsHeartAnimating(false), 500);
  };

  const { cartinfoData, setCartInfoData, user } = useUser();
  const router = useRouter();
  const navigate = (moveon: string) => router.push(moveon);
  const navigatereplace = (moveon: string) => router.replace(moveon);

  const [pageinit, setPageInit] = useState(false);
  const [writereview, setwritereview] = useState(false);
  const userData = useMemo(() => user ?? null, [user]);
  const [productWishlistData, setProductWishlistData] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchWishlistData = async () => {
    await axiosGetWishlist(setProductWishlistData);
  };

  useEffect(() => {
    if (writereview && !userData) router.push("/signin");
  }, [writereview, userData, router]);

  const [availableData, setAvailableData] = useState<AvailableData>({
    colorcodes: [],
    size: [],
    products: [],
    weight: [],
    gravingext: "",
  });

  const [productinfo, setProductInfoData] = useState<Product>(productinfodata);
  const [productinfoselectedSize, setproductinfoselectedSize] = useState<any[]>([]);
  const [productinfoselectedWeight, setproductinfoselectedWeight] = useState<any[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (pageinit) return;

      await fetchWishlistData();
      setproductinfoimages(productinfo);

      await axiosGetProductReviews(
        productinfo?.id,
        setproductaveragerating,
        setproductreviewList
      );

      setProductGroupsData(productgroups);

      const colorIds = [
        { id: productinfo?.colorcodes_id },
        ...(productgroups?.map((pg: any) => ({ id: pg?.colorcodes_id })) || []),
      ].filter(Boolean);

      const sizeIds =
        productinfo?.size_id
          ?.split(",")
          .map((sz: string) => ({ id: Number(sz.trim()) }))
          .filter((s) => !isNaN(s.id)) || [];

      const weightIds = [
        { id: productinfo?.weight_id },
        ...(productgroups?.map((pg: any) => ({ id: pg?.weight_id })) || []),
      ].filter(Boolean);

      setAvailableData({
        colorcodes: colorIds,
        size: sizeIds,
        products: [],
        weight: weightIds,
        gravingext: "",
      });

      FBPIXPush(APPENUMS.EVENT_ENUMS.PRODUCT_DETAILED);
      setPageInit(true);
      setIsLoading(false);
    };

    if (!pageinit && productinfodata?.id) loadData();
  }, [
    pageinit,
    productinfo,
    productgroups,
    productsListData,
    productinfodata?.id,
  ]);

  const [productgroupsData, setProductGroupsData] = useState<any[]>([]);
  const [chooseSizeError, setchooseSizeError] = useState(false);
  const [chooseCoupleSizeError, setchooseCoupleSizeError] = useState(false);
  const [chooseWeightError, setchooseWeightError] = useState(false);
  const [chooseGravingTextError, setgravingTextError] = useState(false);
  const [gravingText, setgravingText] = useState("");
  const [chooseGravingText1Error, setgravingText1Error] = useState(false);
  const [gravingText1, setgravingText1] = useState("");
  const [pselectedSizes, psetselectedSizes] = useState<any>({});
  const [pselectedMenSizes, psetselectedMenSizes] = useState<any>({});
  const [pselectedWomenSizes, psetselectedWomenSizes] = useState<any>({});
  const [pselectedCoupleSizes, psetselectedCoupleSizes] = useState<any>({
    Men: [],
    Women: [],
  });
  const [pselectedWeights, psetselectedWeights] = useState<any>({});
  const [productreviewList, setproductreviewList] = useState([]);
  const [productaveragerating, setproductaveragerating] = useState(0);

  const [count, setCount] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /* --------------------------------------------------------------
     1. REUSABLE: Check if current variation combo is in cart
     -------------------------------------------------------------- */
  const isVariantInCart = useCallback(() => {
    if (!cartinfoData?.products?.length) return false;

    const sel = {
      colorId: productinfo?.colorcodes_id,
      weightId: productinfo?.weight_id,
      sizeId: pselectedSizes?.id,
      menId: pselectedMenSizes?.id,
      womenId: pselectedWomenSizes?.id,
    };

    return cartinfoData.products.some((item: any) => {
      return (
        item.id === productinfo.id &&
        item.colorcodes_id == sel.colorId &&
        item.weight_id == sel.weightId &&
        item.selectedSize?.id == sel.sizeId &&
        item.selectedMenSize?.id == sel.menId &&
        item.selectedWomenSize?.id == sel.womenId
      );
    });
  }, [
    cartinfoData?.products,
    productinfo?.id,
    productinfo?.colorcodes_id,
    productinfo?.weight_id,
    pselectedSizes?.id,
    pselectedMenSizes?.id,
    pselectedWomenSizes?.id,
  ]);

  /* --------------------------------------------------------------
     2. Sync `addtocart` flag on every variation change
     -------------------------------------------------------------- */
  useEffect(() => {
    setProductInfoData((prev) => ({
      ...prev,
      addtocart: isVariantInCart(),
    }));
  }, [isVariantInCart]);

  const assets = process.env.NEXT_PUBLIC_ASSETSSTORE || "";

  const notify = () =>
    toast.success("Added to cart.", {
      style: {
        border: "1px solid #00b852",
        padding: "16px",
        color: "#00b852",
      },
      iconTheme: {
        primary: "#00b852",
        secondary: "#FFFAEE",
      }, duration: 3000,
    });


  const notifyerror = (msg: any) =>
    toast.error(msg, {
      style: { border: "1px solid #cc0000", padding: "16px", color: "#cc0000" },
      iconTheme: { primary: "#cc0000", secondary: "#FFFAEE" },
      duration: 3000,
    });

  const filterSize = (size: any, type: any = null) => {
    if (!size) return true;
    return availableData.size.some(
      (avsize) => avsize.id === size.id && (!type || size.name === type)
    );
  };

  const filterWeight = (weight: any) => {
    if (!weight) return true;
    const isSelected = productinfo?.weight_id === weight.id;
    weight.selected = isSelected;
    return availableData.weight.some((avweight) => avweight.id === weight.id);
  };

  const filterColor = (color: any) => {
    if (!color) return false;
    const isSelected = productinfo?.colorcodes_id === color.id;
    color.selected = isSelected;
    return availableData.colorcodes.some((avcolor) => avcolor.id === color.id);
  };

  const navigateToProduct = (id: any, key: any) => {
    const ongoingProduct = productgroupsData.find((pg) => pg[key] === id);
    if (ongoingProduct) {
      setProductInfoData((prev: any) => ({ ...prev, ...ongoingProduct }));
      setproductinfoimages(ongoingProduct);
      window.history.replaceState({}, "", `/p/${ongoingProduct.seo_url}`);
    }
  };

  const onchangeProductColor = (color: any) => {
    if (productgroupsData.length > 0) navigateToProduct(color.id, "colorcodes_id");
  };

  const onchangeProductWeight = (weight: any) => {
    if (productgroupsData.length > 0) {
      navigateToProduct(weight.id, "weight_id");
    }
    psetselectedWeights(weight);
    weightListData.forEach((w) => {
      w.selected = w.id === weight.id;
    });
  };

  useEffect(() => {
    if (productinfo?.weight_id && weightListData.length > 0) {
      const currentWeight = weightListData.find(w => w.id === productinfo.weight_id);
      if (currentWeight) {
        psetselectedWeights(currentWeight);
        // Also mark in list for UI
        weightListData.forEach(w => w.selected = w.id === currentWeight.id);
      }
    }
  }, [productinfo?.weight_id, weightListData]);

  const setproductinfoimages = (productinfo: any) => {
    if (productinfo?.imageurl) {
      const urls = productinfo.imageurl.split(",").map((url: string) => url.trim());
      const _images = urls.map((imgurl: string) => ({
        original: assets + imgurl,
        thumbnail: assets + imgurl,
      }));
    }
  };

  useEffect(() => {
    if (!pageinit && productsListData.length > 0) setPageInit(true);
  }, [pageinit, productsListData]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const getSizeChartImage = (categoryName: any) => {
    const name = categoryName?.toUpperCase();
    if (name?.includes("KIDS")) return "https://blr1.digitaloceanspaces.com/assetsvilva/tamiltshirts/size-charts/KIDS%20PLAIN%20TSHIRT.png";
    if (name?.includes("HOODED SWEATSHIRTS")) return "https://blr1.digitaloceanspaces.com/assetsvilva/tamiltshirts/size-charts/MEN%20HOODED%20SWEATSHIRT.png";
    if (name?.includes("ZIPPER HOODIES")) return "https://blr1.digitaloceanspaces.com/assetsvilva/tamiltshirts/size-charts/MEN%20ZIPPER%20HOODIES.png";
    return null;
  };

  const [imageSrc, setImageSrc] = useState(
    getSizeChartImage(productinfo?.category_name) || ""
  );

  const [shadow, setShadow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShadow(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const allProducts: Product[] = productsListData;

  const imageUrls: MediaItem[] = useMemo(() => {
    let urls: string[] = [];

    if (typeof productinfo?.imageurl === "string") {
      urls = productinfo.imageurl
        .split(",")
        .map((url: string) => url.trim())
        .filter((url: string) => url.length > 0);
    } else if (Array.isArray(productinfo?.imageurl)) {
      urls = productinfo.imageurl.filter(Boolean);
    }

    if (urls.length === 0) return [];

    return urls.map((url: string, index: number) => {
      const fullUrl = url.startsWith("http") ? url : `${assets}${url}`;
      return {
        id: `${productinfo.id}-${index}`,
        type: fullUrl.match(/\.(mp4|webm)$/i) ? "video" : "image",
        src: url,
        alt: `${productinfo.name} - image ${index + 1}`,
      };
    });
  }, [productinfo?.imageurl, productinfo?.name, productinfo?.id, assets]);

  const goBackOrHome = () => {
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push('/');
    }
  };

  return (
    <>
      {/* Mobile sticky header – unchanged */}
      <div
        className={`block !sticky !top-0 !mb-1 md:!hidden !z-50 !px-5 !py-4 !w-full !bg-white ${shadow ? "!shadow-md" : ""}`}
      >
        <div className="!flex !items-center !justify-between">
          <div>
            <a onClick={goBackOrHome} className="cursor-pointer">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-chevron-left-icon lucide-chevron-left"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </a>
          </div>
          <div className="!flex !items-center !gap-x-4">
            <a
              href="#"
              className="!flex !items-center"
              onClick={(e) => {
                e.preventDefault();
                if (navigator.share) {
                  navigator
                    .share({
                      title: document.title,
                      url: window.location.href,
                    })
                    .catch((err) => {
                      console.error("Error sharing:", err);
                    });
                } else {
                  alert("Your browser does not support the Share feature.");
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide !text-black lucide-share2-icon lucide-share-2"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
                <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
              </svg>
            </a>
            <Link href="/my-wishlist" className="!flex !items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide !text-black lucide-heart-icon lucide-heart"
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5L12 21l7-7.5z"></path>
              </svg>
            </Link>
            <Link href="/cart" className="!relative !flex !items-center">
              <ShoppingCart className="h-5 w-5" />
              {(cartinfoData?.cartcount ?? 0) > 0 && (
                <div className="!absolute !-top-2 !-right-2 !bg-[#dcdf00] !rounded-full !h-[16px] !w-[16px] !flex !justify-center !items-center">
                  <p className="!text-[10px] !text-black !font-semibold">
                    {cartinfoData?.cartcount}
                  </p>
                </div>
              )}
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-4 md:px-6 lg:pb-12">
        <div className="grid md:grid-cols-2 items-start gap-8 lg:gap-12">
          <div className="relative w-full h-fit rounded-lg md:sticky md:top-[90px]">
            <ImageGallery imageUrls={imageUrls} />
          </div>

          <div className="flex flex-col justify-center md:space-y-[16px] space-y-[10px] !relative md:mt-[30px]">
            <div>
              <p className="text-[14px] text-[#737E93] font-medium">
                {productinfo.category_name}
              </p>
              <div className="flex">
                <h1 className="text-[18px] text-black font-bold">
                  {(() => {
                    const tamilName = productinfo.name?.trim();
                    const englishName = productinfo.eng_name?.trim();
                    if (englishName && englishName !== tamilName) {
                      return englishName;
                    }
                    return tamilName;
                  })()}
                </h1>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="">
                <div className="space-x-[6px]">
                  <span className="text-[24px] font-bold text-black">
                    {getCurrencySymbol()} {productinfo.sp}
                  </span>
                  <span className="text-[16px] text-[#737E93] line-through">
                    {getCurrencySymbol()} {productinfo.mrp}
                  </span>
                  {/* <span className="text-[16px] font-medium text-offer_text">
                    {Math.round(((Number(productinfo.mrp) - Number(productinfo.sp)) / Number(productinfo.mrp)) * 100)}% OFF
                  </span> */}
                </div>
                <p className="text-[#737E93] text-[12px]">
                  Inclusive of all taxes
                </p>
              </div>
              {productaveragerating && productaveragerating !== 0 ? (
                <div className="flex space-x-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="yellow"
                    stroke="yellow"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-star-icon lucide-star mt-1"
                  >
                    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                  </svg>
                  <p className="text-gray_text text-[16px]">
                    {productaveragerating.toFixed(1)}
                  </p>
                  <p className="text-[#494949] text-[16px] sm:block hidden">|</p>
                  <p className="text-[#207BB4] text-[16px] sm:block hidden">
                    Reviews
                  </p>
                </div>
              ) : null}
            </div>
            <div>
              {variationsRender(
                variationsListData,
                productinfo,
                colorcodesListData,
                filterColor,
                onchangeProductColor,
                setCount,
                count,
                sizeListData,
                filterSize,
                pselectedSizes,
                psetselectedSizes,
                chooseCoupleSizeError,
                setchooseCoupleSizeError,
                pselectedMenSizes,
                psetselectedMenSizes,
                pselectedWomenSizes,
                psetselectedWomenSizes,
                pselectedCoupleSizes,
                psetselectedCoupleSizes,
                cartinfoData,
                setProductInfoData,
                setproductinfoselectedSize,
                productinfoselectedSize,
                chooseSizeError,
                weightListData,
                filterWeight,
                pselectedWeights,
                psetselectedWeights,
                setproductinfoselectedWeight,
                productinfoselectedWeight,
                onchangeProductWeight,
                chooseWeightError,
                setgravingText,
                chooseGravingTextError,
                isOpen,
                setIsOpen,
                isModalOpen,
                toggleModal,
                setIsModalOpen,
                imageSrc,
                gravingText1,
                setgravingText1,
                chooseGravingText1Error,
                setgravingText1Error
              )}
            </div>

            {/* Add / Go to cart button */}
            <div className="flex space-x-[10px] md:!relative !sticky !top-[0px] !bottom-[0px] bg-white">
              <div className="flex flex-col space-y-[10px] md:w-1/2 w-full">
                <div className="relative">
                  {productinfo?.quantity === 0 ? (
                    <Button
                      className="flex-1 py-[22px] w-full rounded-[8px] bg-red-300 border-2 border-red-300 text-white hover:bg-red-400 text-[16px]"
                      disabled
                    >
                      OUT OF STOCK
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      className={`flex-1 py-[22px] w-full rounded-[8px] ${productinfo?.addtocart
                        ? "!bg-primary_hover text-white hover:bg-primary"
                        : "!bg-primary text-white hover:bg-primary_hover"
                        } text-[16px]`}
                      disabled={isLoading}
                      onClick={async (event: any) => {
                        if (isLoading) return;
                        event.preventDefault();
                        const eventTarget = event.currentTarget;
                        if (eventTarget.readOnly) return;
                        eventTarget.readOnly = true;

                        await addtocartclicks(
                          event,
                          productinfo,
                          sizeListData,
                          setchooseSizeError,
                          chooseCoupleSizeError,
                          setchooseCoupleSizeError,
                          pselectedMenSizes,
                          psetselectedMenSizes,
                          pselectedWomenSizes,
                          psetselectedWomenSizes,
                          pselectedCoupleSizes,
                          psetselectedCoupleSizes,
                          notify,
                          notifyerror,
                          setCount,
                          count,
                          pselectedSizes,
                          pselectedWeights,
                          weightListData,
                          setchooseWeightError,
                          gravingText,
                          setgravingTextError,
                          navigate,
                          setCartInfoData,
                          cartinfoData,
                          gravingText1,
                          setgravingText1,
                          chooseGravingText1Error,
                          setgravingText1Error,
                          setProductInfoData
                        );
                        eventTarget.readOnly = false;
                      }}
                    >
                      {productinfo?.addtocart ? (
                        <> <SvgIcons.GoToCart /> GO TO CART</>
                      ) : (
                        <> <SvgIcons.AddToCart /> ADD TO CART</>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Wishlist & Share */}
              <div className="flex space-x-[10px]">
                <Button
                  onClick={handleWishlistClick}
                  className="flex-1 !w-[70px] py-[22px] px-0 rounded-[8px] border-2 border-[#909090] flex justify-center items-center"
                >
                  <svg
                    className={`cursor-pointer w-[24px] ${isHeartAnimating ? "animate-heart" : ""}`}
                    viewBox="0 0 27 27"
                    fill={productWishlistData.includes(productinfo.id.toString()) ? "#f45440" : "none"}
                    stroke={productWishlistData.includes(productinfo.id.toString()) ? "#f45440" : "#000"}
                    strokeWidth="1.5"
                  >
                    <path d="M13.4993 26.2061L4.70067 16.9253C3.9281 16.1443 3.41815 15.1374 3.24307 14.0471C3.06798 12.9568 3.23664 11.8385 3.72514 10.8505V10.8505C4.09415 10.1046 4.63318 9.45803 5.29779 8.96406C5.96241 8.47008 6.73359 8.14284 7.54782 8.00931C8.36204 7.87578 9.19599 7.93978 9.98095 8.19603C10.7659 8.45228 11.4794 8.89345 12.0627 9.48319L13.4993 10.9358L14.9359 9.48319C15.5192 8.89345 16.2327 8.45228 17.0177 8.19603C17.8026 7.93978 18.6366 7.87578 19.4508 8.00931C20.265 8.14284 21.0362 8.47008 21.7008 8.96406C22.3654 9.45803 22.9045 10.1046 23.2735 10.8505V10.8505C23.762 11.8385 23.9306 12.9568 23.7556 14.0471C23.5805 15.1374 23.0705 16.1443 22.298 16.9253L13.4993 26.2061Z" />
                  </svg>
                </Button>
                <Button className="flex-1 w-full py-[22px] px-0 rounded-[8px] border-2 border-[#909090] flex justify-center items-center">
                  <Share />
                </Button>
              </div>
            </div>

            <CollapsibleSections
              product={productinfo}
              productreviewList={productreviewList}
              setwritereview={setwritereview}
              assets={assets}
              productaveragerating={productaveragerating}
              productinfo={productinfo}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              toggleModal={toggleModal}
            />
          </div>
        </div>

        <RelatedProducts
          allProducts={allProducts}
          currentCategoryId={productinfo.category_id ?? ""}
          currentProductId={productinfo.id}
        />
      </div>

      <style jsx>{`
        @keyframes heartAnimation {
          0% { transform: scale(1); }
          20% { transform: scale(1.3); }
          40% { transform: scale(1.1) rotate(-5deg); }
          60% { transform: scale(1.2) rotate(5deg); }
          80% { transform: scale(1.1) rotate(-3deg); }
          100% { transform: scale(1); }
        }
        .animate-heart { animation: heartAnimation 0.5s ease-in-out; }
      `}</style>
      <Toaster />
    </>
  );
}